import { Injectable } from '@angular/core';
import { CommunicationManagerService } from '../services/communication-manager.service';
import { BankAccountService } from './bank-account.service';
import { BankAccount } from '../models/bank-account';
import { CreditCardService } from './credit-card.service';
import { CreditCard } from '../models/credit-card.model';
import { CollaboratorService } from './collaborator.service';
import { Collaborator } from '../models/collaborator.model';
import { firstValueFrom } from 'rxjs';
import { UsersService } from './user.service';
import { LocalStorageService, NameFilter, User } from '@ops/shared-types';
import { TranslateService } from '@ngx-translate/core';

export interface NameIdPair {
  id: string;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class EnumService {
  //TODO: get all from DB
  paymentMethodOptions: Array<string> = [
    'Bank Transfer',
    'Credit Card',
    'Debit / Cash',
    'Personal Money',
    // "enum.bankTransfer",
    // "enum.creditCard",
    // "enum.debitCash",
    // "enum.personalMoney"
  ];

  costTypeOptions: Array<string> = ['In-House', 'Freelance', 'Expense'];

  costStatusOptions: Array<string> = [
    'Paid',
    'TBC',
    'Budgeted',
    'Reimbursement',
  ];

  assignToOptions: Array<string> = [
    'Yuko',
    'Nikola',
    'Ayako',
    'Akiko',
    'Miki',
    'Ching',
  ];

  users: Array<string> = ['Yuko', 'Nikola', 'Ayako', 'Akiko', 'Miki', 'Ching'];

  accountingCategoryOptions: Array<string> = [
    'Payroll',
    'Outsourcing Salary',
    'Rent / Utilities',
    'G&A / Office Expense',
    'Travel & Transportation',
    'Professional Services',
    'Tax / Insurance',
    'Other',
  ];

  subcategoryOptions: Array<{ category: string; options: Array<string> }> = [
    {
      category: 'PAYROLL',
      options: [
        'Net Salary',
        'Overtime Fee',
        'Health',
        'Social & Pension Premiums',
        'Bonus',
      ],
    },
    {
      category: 'Outsourcing Salary',
      options: [
        'Hourly Fee',
        'Monthly Fee',
        'Fixed Fee',
        'Withholding Income Tax',
        'Overtime Fee',
      ],
    },
    {
      category: 'Rent / Utilities',
      options: [
        'Office Rent',
        'Studio Rent',
        'Warehouse Rent',
        'Utilities (electricity, water, gas, internet)',
        'Director’s Housing Rent',
        'Employee Housing Rent',
        'Other',
      ],
    },
    {
      category: 'G&A / Office Expense',
      options: [
        'Office supplies (<¥100,000)',
        'Office equipment (>¥100,000)',
        'Telephone Communication',
        'Mobile Communication',
        'Postage & Courier Fees',
        'Books & Magazines',
        'Maintenance and Repairs',
        'Business Meetings',
        'Lunch & Entertainment',
        'Subscriptions',
        'Digital Tools',
        'Memberships',
        'Bank, Transfer Charges',
        'Other',
        // "enum.gaOfficeSupplies",
        // "enum.gaOfficeEq",
        // "enum.gaTelephoneCom",
        // "enum.gaMobileCom",
        // "enum.gaPostageCour",
        // "enum.gaBooksnMag",
        // "enum.gaMaintenancenRep",
        // "enum.gaBusinessMeetings",
        // "enum.gaLunchNEnt",
        // "enum.gaSubscriptions",
        // "enum.gaDigitalTools",
        // "enum.gaMemberships",
        // "enum.gaBankTransferCharges",
        // "enum.gaOther",
      ],
    },
    {
      category: 'Travel & Transportation',
      options: [
        'Transportation (Train, Taxi, Airplane, Boat)',
        'Transportation (Car, Van, Vehicle)',
        'Transportation Charges (Gasoline, Tolls, Electricity, Licence)',
        'Accommodation (Hotel, Private Stay, Airbnb)',
        'Other',
      ],
    },
    {
      category: 'Professional Services',
      options: [
        'Accounting and Auditing',
        'Legal Services',
        'Consulting Services',
        'IT Services',
        'WEB & Design Services',
        'PR & Advertising Services',
        'Other',
      ],
    },
    {
      category: 'Tax / Insurance',
      options: [
        'Income Tax',
        'Sales Tax',
        'Property Tax',
        'Withholding Tax',
        'Insurance Premium',
        'Loan Interest ',
        'Load Repayment',
        'Work Contract Obligation',
        'Other',
      ],
    },
    {
      category: 'Other',
      options: [
        'Expense Reimbursement',
        'Petty Cash Withdrawal',
        'Debit Card Charge',
        'Credit Card Settlement',
        'Internal Bank Transfer',
      ],
    },
  ];

  currencyOptions: Array<string> = ['USD', 'EUR', 'GBP', 'JPY', 'RSD', 'CNY'];
  
  currenciesOptionsDetailed = [
    { name: 'United States Dollar', shortName: 'USD', code: '$' },
    { name: 'Euro', shortName: 'EUR', code: '€' },
    { name: 'British Pound Sterling', shortName: 'GBP', code: '£' },
    { name: 'Japanese Yen', shortName: 'JPY', code: '¥' },
    { name: 'Serbian Dinar', shortName: 'RSD', code: 'Д' },
    { name: 'Chinese Yuan', shortName: 'CNY', code: 'CNY' },
  ];

  // Custom currencies storage
  private customCurrencies: Array<{ name: string; shortName: string; code: string }> = [];

  //    TODO : 'weekly', review logic
  recurringFrequencyOptions: Array<string> = ['daily', 'monthly', 'yearly'];

  projectScopeOptions: Array<string> = [
    'Photo',
    'Video',
    'Event',
    'Music',
    'Print',
    'Digital',
  ];


  paidByOptions: Array<string> | undefined;

  // Cache properties
  private bankAccountsCache: Map<string, Array<BankAccount>> = new Map();
  private creditCardsCache: Map<string, Array<CreditCard>> = new Map();
  private usersCache: Map<string, Array<User>> = new Map();
  private currentCompanyId: string | null = null;

  constructor(
    private cm: CommunicationManagerService,
    private bankAccountService: BankAccountService,
    private creditCardService: CreditCardService,
    private userService: UsersService,
    private localStorageService: LocalStorageService,
    private translateService: TranslateService
  ) {
    this.initializeCustomCurrencies();
  }

  getAllOpsEnumsFromDB() {
    this.cm.get('enum', []).subscribe((enums) => {
      //TODO: map enums from BE
    });
  }

  /**
   * Cache management methods
   */
  private async getCachedBankAccounts(companyId: string): Promise<Array<BankAccount>> {
    if (!this.bankAccountsCache.has(companyId)) {
      const accounts = await firstValueFrom(
        this.bankAccountService.getAllBankAccountsForCompany(companyId)
      );
      this.bankAccountsCache.set(companyId, accounts);
    }
    return this.bankAccountsCache.get(companyId)!;
  }

  private async getCachedCreditCards(companyId: string): Promise<Array<CreditCard>> {
    if (!this.creditCardsCache.has(companyId)) {
      const cards = await firstValueFrom(
        this.creditCardService.getCreditCardsForCompany(companyId)
      );
      this.creditCardsCache.set(companyId, cards);
    }
    return this.creditCardsCache.get(companyId)!;
  }

  private async getCachedUsers(companyId: string): Promise<Array<User>> {
    if (!this.usersCache.has(companyId)) {
      const users = await firstValueFrom(
        this.userService.getUsersForCompany(companyId)
      );
      this.usersCache.set(companyId, users);
    }
    return this.usersCache.get(companyId)!;
  }

  /**
   * Clear cache when company changes
   */
  public clearCache(companyId?: string): void {
    if (companyId && companyId !== this.currentCompanyId) {
      this.bankAccountsCache.clear();
      this.creditCardsCache.clear();
      this.usersCache.clear();
      this.currentCompanyId = companyId;
    } else if (!companyId) {
      this.bankAccountsCache.clear();
      this.creditCardsCache.clear();
      this.usersCache.clear();
      this.currentCompanyId = null;
    }
  }

  /**
   * Refresh cache for specific company
   */
  public async refreshCache(companyId: string): Promise<void> {
    this.bankAccountsCache.delete(companyId);
    this.creditCardsCache.delete(companyId);
    this.usersCache.delete(companyId);
    
    // Preload data for the company
    await Promise.all([
      this.getCachedBankAccounts(companyId),
      this.getCachedCreditCards(companyId),
      this.getCachedUsers(companyId)
    ]);
  }

  /**
   * Preload data for a company to improve performance
   */
  public async preloadCompanyData(companyId: string): Promise<void> {
    if (companyId !== this.currentCompanyId) {
      this.clearCache(companyId);
      await Promise.all([
        this.getCachedBankAccounts(companyId),
        this.getCachedCreditCards(companyId),
        this.getCachedUsers(companyId)
      ]);
    }
  }

  /**
   * Get cache statistics for debugging
   */
  public getCacheStats(): {
    bankAccountsCacheSize: number;
    creditCardsCacheSize: number;
    usersCacheSize: number;
    currentCompanyId: string | null;
  } {
    return {
      bankAccountsCacheSize: this.bankAccountsCache.size,
      creditCardsCacheSize: this.creditCardsCache.size,
      usersCacheSize: this.usersCache.size,
      currentCompanyId: this.currentCompanyId,
    };
  }

  getAllDepartments(projectId: string) {
    return this.cm.get('department/all', [
      { headerName: 'projectId', headerValue: projectId },
    ]);
  }

  getCostTypeOptions(): Array<string> {
    return this.costTypeOptions.sort((a, b) => {
      return a.localeCompare(b); // Sort the rest in ascending order
    });
    // return this.costTypeOptions;
  }

  getCostStatuseOptions(): Array<string> {
    return this.costStatusOptions.sort((a, b) => {
      return a.localeCompare(b); // Sort the rest in ascending order
    });
    // return this.costStatusOptions;
  }

  getPaymentMethodOptions(): Array<string> {
    return this.paymentMethodOptions.sort((a, b) => {
      return a.localeCompare(b); // Sort the rest in ascending order
    });
    // return this.paymentMethodOptions;
  }

  /**
   * Get payment method options filtered by what's actually available for the organization
   * This prevents showing "Credit Card" or "Bank Transfer" options when no accounts/cards exist
   */
  async getFilteredPaymentMethodOptions(companyId: string): Promise<Array<string>> {
    const allMethods = this.getPaymentMethodOptions();
    const filteredMethods: string[] = [];

    for (const method of allMethods) {
      try {
        const pair = await this.getPaidByOptions(method, companyId);
        if (pair.optionsList.length > 0) {
          filteredMethods.push(method);
        }
      } catch (error) {
        console.warn(`Failed to check options for ${method}:`, error);
      }
    }

    return filteredMethods;
  }

  /**
   * Get all payment method options (unfiltered) - for cases where you want to show all methods
   * even if no accounts/cards are configured
   */
  getAllPaymentMethodOptions(): Array<string> {
    return this.getPaymentMethodOptions();
  }

  async getPaidByOptions(
    paymentMethod: string,
    companyId: string
  ): Promise<{
    paidByList: Array<{ id: string; name: string; currencyCode?: string }>;
    optionsList: Array<string>;
  }> {
    // Clear cache if company changed
    this.clearCache(companyId);

    const baseCurrencyCode =
      this.localStorageService.getCurrentCompany()?.baseCurrencyCode || 'JPY';

    let paidByList: Array<{
      id: string;
      name: string;
      currencyCode?: string;
      isActive?: boolean;
    }> = [];

    let paidByListSorted: Array<{
      id: string | undefined;
      name: string | undefined;
      currencyCode?: string;
      isActive?: boolean;
    }> = [];

    let paidByOptions: Array<string | undefined> = [];

    switch (paymentMethod?.toUpperCase()) {
      case 'BANK TRANSFER': {
        const accounts = await this.getCachedBankAccounts(companyId);
        paidByList = this.processBankAccountsForTransfer(accounts);
        paidByListSorted = this.sortCurrencyCardOptions(paidByList, baseCurrencyCode);
        paidByOptions = paidByListSorted.map((a) => a.name);
        break;
      }
      case 'CREDIT CARD': {
        const cards = await this.getCachedCreditCards(companyId);
        paidByList = this.processCreditCards(cards);
        paidByListSorted = this.sortCurrencyCardOptions(paidByList, baseCurrencyCode);
        paidByOptions = paidByListSorted
          .filter((onlyActive) => onlyActive.isActive)
          .map((a) => a.name);
        break;
      }
      case 'DEBIT / CASH': {
        const accounts = await this.getCachedBankAccounts(companyId);
        paidByList = this.processBankAccountsForDebitCash(accounts);
        paidByListSorted = this.sortCurrencyCardOptions(paidByList, baseCurrencyCode);
        paidByOptions = paidByListSorted.map((a) => a.name);
        break;
      }
      case 'PERSONAL MONEY': {
        const users = await this.getCachedUsers(companyId);
        const companyUsers = this.filterCompanyUsers(users, companyId);
        paidByList = this.processUsers(companyUsers);
        paidByListSorted = this.sortUsersOptions(paidByList);
        paidByOptions = paidByList.map((a) => a.name);
        break;
      }
      default: {
        this.paidByOptions = [];
      }
    }

    return {
      paidByList: paidByListSorted as unknown as any,
      optionsList: this.filterOptions(paidByOptions),
    };
  }

  getRecurringFrequencyOptions(): Array<string> {
    return this.recurringFrequencyOptions.sort((a, b) => {
      return a.localeCompare(b); // Sort the rest in ascending order
    });
    // return this.recurringFrequencyOptions;
  }

  async getAssignToOptions(companyId: string): Promise<Array<NameIdPair>> {
    return (
      await firstValueFrom(this.userService.getUsersForCompany(companyId))
    )
      .map((u) => {
        return { id: u._id, name: `${u.firstName} ${u.lastName}` };
      })
      .sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
  }

  getUsers() {
    return this.users;
  }

  getAccountingCategoryOptions(): Array<string> {
    return this.accountingCategoryOptions.sort((a, b) => a.localeCompare(b));
  }

  getSubcategoryOptions(): Array<{ category: string; options: Array<string> }> {
    return this.subcategoryOptions;
  }

  // sortCurrencyCardOptions(listToSort: any[], firstToShow: string) {
  //   return listToSort.sort((a, b) => {
  //     if (a.currencyCode === firstToShow) return -1; // Put the input value first
  //     if (b.currencyCode === firstToShow) return 1;
  //     return a?.currencyCode?.localeCompare(b?.currencyCode); // Sort the rest in ascending order
  //   });
  // }
  sortCurrencyCardOptions(
    items: Array<{
      id: string;
      name: string;
      currencyCode?: string;
      isActive?: boolean;
    }>,
    currencyCode: string
  ) {
    return items.sort((a, b) => {
      // Push items with the specified currencyCode to the top
      if (a.currencyCode === currencyCode && b.currencyCode !== currencyCode)
        return -1;
      if (b.currencyCode === currencyCode && a.currencyCode !== currencyCode)
        return 1;

      // If both have the specified currencyCode
      if (a.currencyCode === currencyCode && b.currencyCode === currencyCode) {
        // Check if the name includes the currencyCode
        const aIncludesCurrency = a.name
          .toUpperCase()
          .includes(currencyCode.toUpperCase());
        const bIncludesCurrency = b.name
          .toUpperCase()
          .includes(currencyCode.toUpperCase());

        // Push items with currencyCode in the name to the top
        if (aIncludesCurrency && !bIncludesCurrency) return -1;
        if (bIncludesCurrency && !aIncludesCurrency) return 1;
      }

      // Sort the rest by name in ascending order
      return a.name.localeCompare(b.name);
    });
  }

  sortUsersOptions(items: any[]) {
    return items.sort((a, b) => {
      return a.name.localeCompare(b.name); // Sort the rest in ascending order
    });
  }

  sortCurrencyOptions(firstToShow?: string) {
    return this.currencyOptions.sort((a, b) => {
      if (firstToShow && a === firstToShow) return -1; // Put the input value first
      if (firstToShow && b === firstToShow) return 1;
      return a.localeCompare(b); // Sort the rest in ascending order
    });
  }

  sortCurrenciesOptionsDetailed(firstToShow?: string) {
    return this.currenciesOptionsDetailed.sort((a, b) => {
      if (firstToShow && a.shortName === firstToShow) return -1; // Put the input value first
      if (firstToShow && b.shortName === firstToShow) return 1;
      return a.shortName.localeCompare(b.shortName); // Sort the rest in ascending order
    });
  }

  getCurrencyOptions(excludeBaseCurrency?: boolean): Array<string> {
    const baseCurrencyCode =
      this.localStorageService.getCurrentCompany()?.baseCurrencyCode;

    // Get all currencies (default + custom)
    const allCurrencies = this.getAllCurrencies();
    
    // Sort currencies with base currency first
    const sortedCurrencies = allCurrencies.sort((a, b) => {
      if (baseCurrencyCode && a === baseCurrencyCode) return -1;
      if (baseCurrencyCode && b === baseCurrencyCode) return 1;
      return a.localeCompare(b);
    });
    
    if (excludeBaseCurrency) {
      const withoutFirstCurrency = [...sortedCurrencies];
      withoutFirstCurrency.shift();
      return withoutFirstCurrency;
    }
    return sortedCurrencies;
  }

  getCurrencyOptionsDetailed(): Array<any> {
    const baseCurrencyCode =
      this.localStorageService.getCurrentCompany()?.baseCurrencyCode;

    // Get all currencies detailed (default + custom)
    const allCurrenciesDetailed = this.getAllCurrenciesDetailed();
    
    return allCurrenciesDetailed.sort((a, b) => {
      if (baseCurrencyCode && a.shortName === baseCurrencyCode) return -1;
      if (baseCurrencyCode && b.shortName === baseCurrencyCode) return 1;
      return a.shortName.localeCompare(b.shortName);
    });
  }

  getProjectScopeOptions(): Array<string> {
    return this.projectScopeOptions.map((option) => {
      return this.translateService.instant(option);
    });
  }

  /**
   * Helper methods for data processing
   */
  private processBankAccountsForTransfer(accounts: Array<BankAccount>): Array<{
    id: string;
    name: string;
    currencyCode?: string;
  }> {
    return accounts
      .filter((a) => !a.isDebit)
      .filter((a: any) => !['DEBIT', 'CASH', 'TEMP'].includes(a.branchName?.toUpperCase()))
      .map((a: BankAccount) => ({
        id: a._id,
        name: a.shortName,
        currencyCode: a.currencyCode,
      }));
  }

  private processBankAccountsForDebitCash(accounts: Array<BankAccount>): Array<{
    id: string;
    name: string;
    currencyCode?: string;
  }> {
    return accounts
      .filter((a) => a.isDebit)
      .filter((a: any) => ['DEBIT', 'CASH'].includes(a.branchName?.toUpperCase()))
      .map((a: BankAccount) => ({
        id: a._id,
        name: a.shortName,
        currencyCode: a.currencyCode,
      }));
  }

  private processCreditCards(cards: Array<CreditCard>): Array<{
    id: string;
    name: string;
    currencyCode?: string;
    isActive?: boolean;
  }> {
    return cards.map((c: CreditCard) => ({
      id: c._id || '',
      name: c.shortName || '',
      currencyCode: c.currencyCode,
      isActive: c.isActive,
    }));
  }

  private filterCompanyUsers(users: Array<User>, companyId: string): Array<User> {
    return users.filter((u) => {
      const companyMatch = u?.companies?.filter((cItem) => cItem?.company === companyId) || [];
      return companyMatch.length > 0;
    });
  }

  private processUsers(users: Array<User>): Array<{
    id: string;
    name: string;
  }> {
    return users.map((u: User) => ({
      id: u._id,
      name: `${u.firstName} ${u.lastName}`,
    }));
  }

  private filterOptions(options: Array<string | undefined>): Array<string> {
    return options
      .filter((o: any) => !['AMEX', 'JAL'].includes(o))
      .filter((o: any) => !o.startsWith('TEMP', 0)) as unknown[] as string[];
  }

  /**
   * Custom currency management methods
   */
  
  /**
   * Validates if a currency code is valid (3 letters, uppercase, English alphabet)
   */
  validateCustomCurrency(currencyCode: string): { isValid: boolean; error?: string } {
    if (!currencyCode) {
      return { isValid: false, error: 'Currency code is required' };
    }
    
    if (currencyCode.length !== 3) {
      return { isValid: false, error: this.translateService.instant('common.currencyCodeLengthError') };
    }
    
    if (currencyCode !== currencyCode.toUpperCase()) {
      return { isValid: false, error: 'Currency code must be in uppercase' };
    }
    
    if (!/^[A-Z]{3}$/.test(currencyCode)) {
      return { isValid: false, error: 'Currency code must contain only English alphabet letters' };
    }
    
    // Check if currency already exists (default or custom)
    const allCurrencies = [...this.currencyOptions, ...this.customCurrencies.map(c => c.shortName)];
    if (allCurrencies.includes(currencyCode)) {
      return { isValid: false, error: 'Currency code already exists' };
    }
    
    return { isValid: true };
  }

  /**
   * Adds a custom currency
   */
  addCustomCurrency(name: string, shortName: string, code: string): { success: boolean; error?: string } {
    const validation = this.validateCustomCurrency(shortName);
    if (!validation.isValid) {
      return { success: false, error: validation.error };
    }
    
    if (!name || name.trim().length === 0) {
      return { success: false, error: 'Currency name is required' };
    }
    
    if (!code || code.trim().length === 0) {
      return { success: false, error: 'Currency symbol is required' };
    }
    
    const customCurrency = {
      name: name.trim(),
      shortName: shortName.toUpperCase(),
      code: code.trim()
    };
    
    this.customCurrencies.push(customCurrency);
    
    // Persist to localStorage
    this.saveCustomCurrencies();
    
    return { success: true };
  }

  /**
   * Removes a custom currency
   */
  removeCustomCurrency(shortName: string): boolean {
    const index = this.customCurrencies.findIndex(c => c.shortName === shortName);
    if (index !== -1) {
      this.customCurrencies.splice(index, 1);
      this.saveCustomCurrencies();
      return true;
    }
    return false;
  }

  /**
   * Gets all custom currencies
   */
  getCustomCurrencies(): Array<{ name: string; shortName: string; code: string }> {
    return [...this.customCurrencies];
  }

  /**
   * Gets all currencies (default + custom)
   */
  getAllCurrencies(): Array<string> {
    const customCurrencyCodes = this.customCurrencies.map(c => c.shortName);
    return [...this.currencyOptions, ...customCurrencyCodes];
  }

  /**
   * Gets all currencies detailed (default + custom)
   */
  getAllCurrenciesDetailed(): Array<{ name: string; shortName: string; code: string }> {
    return [...this.currenciesOptionsDetailed, ...this.customCurrencies];
  }

  /**
   * Saves custom currencies to localStorage
   */
  private saveCustomCurrencies(): void {
    try {
      localStorage.setItem('ops_custom_currencies', JSON.stringify(this.customCurrencies));
    } catch (error) {
      console.error('Failed to save custom currencies:', error);
    }
  }

  /**
   * Loads custom currencies from localStorage
   */
  private loadCustomCurrencies(): void {
    try {
      const saved = localStorage.getItem('ops_custom_currencies');
      if (saved) {
        this.customCurrencies = JSON.parse(saved);
      }
    } catch (error) {
      console.error('Failed to load custom currencies:', error);
      this.customCurrencies = [];
    }
  }

  /**
   * Initialize custom currencies (call this in constructor)
   */
  private initializeCustomCurrencies(): void {
    this.loadCustomCurrencies();
  }
}
